import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInvoice } from '@/hooks/useInvoice';
import { useQuotation } from '@/hooks/useQuotation';
import { useSubscription } from '@/hooks/useSubscription';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { db } from '@/lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import type { Tax, TaxDiscountType, Business, Customer, InventoryItem } from '@/types/invoice';
import { isValidEmail, isValidPhone, isValidStripeLink, isValidPaypalLink } from '@/lib/validation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  FileText,
  Plus,
  DollarSign,
  CheckCircle2,
  Clock,
  XCircle,
  Eye,
  Edit,
  Building2,
  Users,
  Package,
  Percent,
  Crown,
  Trash2,
  Upload,
  ImageIcon,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { cn, formatCurrency, compressLogo } from '@/lib/utils';
import { format } from 'date-fns';

export default function Dashboard() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { 
    invoices, 
    loading: invoicesLoading, 
    createBusiness, 
    updateBusiness,
    deleteBusiness,
    createCustomer, 
    updateCustomer,
    deleteCustomer,
    createInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    businesses,
    customers,
    inventoryItems,
    updateInvoice
  } = useInvoice();
  const { user, userProfile, refreshUserProfile } = useAuth();
  const { quotations, loading: quotationsLoading, updateQuotation } = useQuotation();
  const { isPro } = useSubscription();

  // Tax settings states
  const [showTaxDialog, setShowTaxDialog] = useState(false);
  const [editingTaxIndex, setEditingTaxIndex] = useState<number | null>(null);
  const [taxForm, setTaxForm] = useState<{ label: string; type: TaxDiscountType; value: number | string }>({
    label: '',
    type: 'percentage',
    value: '',
  });
  const [savingTax, setSavingTax] = useState(false);

  const handleAddTax = () => {
    setEditingTaxIndex(null);
    setTaxForm({
      label: '',
      type: 'percentage',
      value: '',
    });
    setShowTaxDialog(true);
  };

  const handleEditTax = (index: number) => {
    if (!userProfile?.predefinedTaxes) return;
    const tax = userProfile.predefinedTaxes[index];
    setEditingTaxIndex(index);
    setTaxForm({
      label: tax.label,
      type: tax.type,
      value: tax.value,
    });
    setShowTaxDialog(true);
  };

  const handleDeleteTax = async (index: number) => {
    if (!user || !userProfile?.predefinedTaxes) return;
    if (confirm('Are you sure you want to delete this predefined tax rate?')) {
      try {
        const updatedTaxes = userProfile.predefinedTaxes.filter((_, i) => i !== index);
        await updateDoc(doc(db, 'users', user.uid), {
          predefinedTaxes: updatedTaxes
        });
        toast({
          title: 'Tax Setup Saved',
          description: 'Predefined tax rate deleted successfully.',
        });
        await refreshUserProfile();
      } catch (err) {
        console.error('Error deleting predefined tax:', err);
        toast({
          title: 'Error',
          description: 'Failed to delete predefined tax',
          variant: 'destructive',
        });
      }
    }
  };

  const handleSaveTax = async () => {
    if (!user || !taxForm.label || taxForm.value === '') {
      toast({ title: 'Error', description: 'Label and value are required', variant: 'destructive' });
      return;
    }
    setSavingTax(true);
    try {
      const taxItem: Tax = {
        label: taxForm.label,
        type: taxForm.type,
        value: Number(taxForm.value) || 0,
        enabled: true,
      };

      let updatedTaxes = [...(userProfile?.predefinedTaxes || [])];
      if (editingTaxIndex !== null) {
        updatedTaxes[editingTaxIndex] = taxItem;
      } else {
        updatedTaxes.push(taxItem);
      }

      await updateDoc(doc(db, 'users', user.uid), {
        predefinedTaxes: updatedTaxes
      });

      toast({
        title: 'Tax Setup Saved',
        description: 'Predefined tax rate saved successfully.',
      });

      await refreshUserProfile();
      setShowTaxDialog(false);
    } catch (err) {
      console.error('Error saving predefined tax:', err);
      toast({
        title: 'Error',
        description: 'Failed to save predefined tax',
        variant: 'destructive',
      });
    } finally {
      setSavingTax(false);
    }
  };

  // Business dialog states
  const [showBusinessDialog, setShowBusinessDialog] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [businessForm, setBusinessForm] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    website: '',
    taxNumber: '',
    bankDetails: '',
    logoUrl: '',
    isDefault: false,
    stripePaymentLink: '',
    paypalLink: '',
    disableAutoApplyPaymentLinks: false,
  });
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [businessErrors, setBusinessErrors] = useState<{
    name?: boolean;
    email?: boolean;
    phone?: boolean;
    stripePaymentLink?: boolean;
    paypalLink?: boolean;
  }>({});

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload an image file.',
        variant: 'destructive',
      });
      return;
    }

    setLogoUploading(true);
    try {
      const base64Url = await compressLogo(file);
      setBusinessForm(prev => ({ ...prev, logoUrl: base64Url }));
      toast({
        title: 'Logo Uploaded',
        description: 'Business logo has been processed and saved successfully.',
      });
    } catch (error) {
      console.error('Error processing logo:', error);
      toast({
        title: 'Upload Failed',
        description: 'Failed to process logo image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setBusinessForm(prev => ({ ...prev, logoUrl: '' }));
    toast({
      title: 'Logo Removed',
      description: 'Business logo has been removed.',
    });
  };

  const handleAddBusiness = () => {
    setEditingBusiness(null);
    setBusinessForm({
      name: '',
      address: '',
      email: '',
      phone: '',
      website: '',
      taxNumber: '',
      bankDetails: '',
      logoUrl: '',
      isDefault: businesses.length === 0,
      stripePaymentLink: '',
      paypalLink: '',
      disableAutoApplyPaymentLinks: false,
    });
    setBusinessErrors({});
    setShowBusinessDialog(true);
  };

  const handleEditBusiness = (business: Business) => {
    setEditingBusiness(business);
    setBusinessForm({
      name: business.name,
      address: business.address || '',
      email: business.email,
      phone: business.phone || '',
      website: business.website || '',
      taxNumber: business.taxNumber || '',
      bankDetails: business.bankDetails || '',
      logoUrl: business.logoUrl || '',
      isDefault: business.isDefault || false,
      stripePaymentLink: (business as any).stripePaymentLink || '',
      paypalLink: (business as any).paypalLink || '',
      disableAutoApplyPaymentLinks: business.disableAutoApplyPaymentLinks || false,
    });
    setBusinessErrors({});
    setShowBusinessDialog(true);
  };

  const handleDeleteBusiness = async (businessId: string) => {
    if (confirm('Are you sure you want to delete this business profile?')) {
      await deleteBusiness(businessId);
    }
  };

  const handleSaveBusiness = async () => {
    const errors: typeof businessErrors = {};
    let hasError = false;

    if (!businessForm.name) {
      errors.name = true;
      hasError = true;
    }
    if (!businessForm.email || !isValidEmail(businessForm.email)) {
      errors.email = true;
      hasError = true;
    }
    if (businessForm.phone && !isValidPhone(businessForm.phone)) {
      errors.phone = true;
      hasError = true;
    }
    if (businessForm.stripePaymentLink && !isValidStripeLink(businessForm.stripePaymentLink)) {
      errors.stripePaymentLink = true;
      hasError = true;
    }
    if (businessForm.paypalLink && !isValidPaypalLink(businessForm.paypalLink)) {
      errors.paypalLink = true;
      hasError = true;
    }

    setBusinessErrors(errors);

    if (hasError) {
      toast({
        title: 'Validation Error',
        description: 'Please correct the highlighted fields in red before saving.',
        variant: 'destructive',
      });
      return;
    }

    setSavingBusiness(true);
    try {
      if (editingBusiness) {
        await updateBusiness(editingBusiness.businessId, businessForm);
        toast({ title: 'Success', description: 'Business profile updated successfully' });
      } else {
        await createBusiness(businessForm);
        toast({ title: 'Success', description: 'Business profile created successfully' });
      }
      setShowBusinessDialog(false);
    } catch (error) {
      // Handled by hook
    } finally {
      setSavingBusiness(false);
    }
  };

  // Customer dialog states
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    companyName: '',
    address: '',
    email: '',
    phone: '',
    taxNumber: '',
    isDefault: false,
  });
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerErrors, setCustomerErrors] = useState<{
    name?: boolean;
    email?: boolean;
    phone?: boolean;
  }>({});

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setCustomerForm({
      name: '',
      companyName: '',
      address: '',
      email: '',
      phone: '',
      taxNumber: '',
      isDefault: customers.length === 0,
    });
    setCustomerErrors({});
    setShowCustomerDialog(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      name: customer.name,
      companyName: customer.companyName || '',
      address: customer.address || '',
      email: customer.email,
      phone: customer.phone || '',
      taxNumber: customer.taxNumber || '',
      isDefault: customer.isDefault || false,
    });
    setCustomerErrors({});
    setShowCustomerDialog(true);
  };

  const handleDeleteCustomer = async (customerId: string) => {
    if (confirm('Are you sure you want to delete this customer profile?')) {
      await deleteCustomer(customerId);
    }
  };

  const handleSaveCustomer = async () => {
    const errors: typeof customerErrors = {};
    let hasError = false;

    if (!customerForm.name) {
      errors.name = true;
      hasError = true;
    }
    if (!customerForm.email || !isValidEmail(customerForm.email)) {
      errors.email = true;
      hasError = true;
    }
    if (customerForm.phone && !isValidPhone(customerForm.phone)) {
      errors.phone = true;
      hasError = true;
    }

    setCustomerErrors(errors);

    if (hasError) {
      toast({
        title: 'Validation Error',
        description: 'Please correct the highlighted fields in red before saving.',
        variant: 'destructive',
      });
      return;
    }

    setSavingCustomer(true);
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.customerId, customerForm);
        toast({ title: 'Success', description: 'Customer profile updated successfully' });
      } else {
        await createCustomer(customerForm);
        toast({ title: 'Success', description: 'Customer profile created successfully' });
      }
      setShowCustomerDialog(false);
    } catch (error) {
      // Handled by hook
    } finally {
      setSavingCustomer(false);
    }
  };

  // Item Inventory states
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [itemForm, setItemForm] = useState({
    code: '',
    name: '',
    description: '',
    rate: 0 as number | string,
    unit: 'pcs',
  });
  const [savingItem, setSavingItem] = useState(false);

  const handleAddItem = () => {
    setEditingItem(null);
    setItemForm({
      code: '',
      name: '',
      description: '',
      rate: 0,
      unit: 'pcs',
    });
    setShowItemDialog(true);
  };

  const handleEditItem = (item: InventoryItem) => {
    setEditingItem(item);
    setItemForm({
      code: item.code,
      name: item.name,
      description: item.description || '',
      rate: item.rate,
      unit: item.unit || 'pcs',
    });
    setShowItemDialog(true);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (confirm('Are you sure you want to delete this inventory item or service?')) {
      await deleteInventoryItem(itemId);
    }
  };

  const handleSaveItem = async () => {
    if (!itemForm.code || !itemForm.name) return;
    setSavingItem(true);
    try {
      const sanitizedItemForm = {
        ...itemForm,
        rate: Number(itemForm.rate) || 0
      };
      if (editingItem) {
        await updateInventoryItem(editingItem.itemId, sanitizedItemForm);
        toast({ title: 'Success', description: 'Item or service updated successfully' });
      } else {
        await createInventoryItem(sanitizedItemForm);
        toast({ title: 'Success', description: 'Item or service created successfully' });
      }
      setShowItemDialog(false);
    } catch (error) {
      // Handled by hook
    } finally {
      setSavingItem(false);
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId: string, newStatus: any) => {
    try {
      await updateInvoice(invoiceId, { status: newStatus });
    } catch (error) {
      console.error('Failed to update invoice status:', error);
    }
  };

  const handleUpdateQuotationStatus = async (quotationId: string, newStatus: any) => {
    try {
      await updateQuotation(quotationId, { status: newStatus });
    } catch (error) {
      console.error('Failed to update quotation status:', error);
    }
  };

  const loading = invoicesLoading || quotationsLoading;

  // Calculate revenue by currency
  const paidInvoices = invoices.filter((inv) => inv.status === 'paid');
  const revenueByCurrency = paidInvoices.reduce((acc, inv) => {
    const currency = inv.currency || 'MYR';
    acc[currency] = (acc[currency] || 0) + (inv.totalAmount || 0);
    return acc;
  }, {} as Record<string, number>);

  const currencies = Object.keys(revenueByCurrency);
  const primaryCurrency = currencies[0] || 'MYR';
  const hasMixedCurrencies = currencies.length > 1;

  const stats = {
    total: invoices.length,
    paid: paidInvoices.length,
    unpaid: invoices.filter((inv) => inv.status === 'sent' || inv.status === 'draft').length,
    draft: invoices.filter((inv) => inv.status === 'draft').length,
    revenueByCurrency,
    primaryCurrency,
    hasMixedCurrencies,
  };

  const recentInvoices = [...invoices]
    .sort((a, b) => {
      const aTime = a.updatedAt instanceof Date ? a.updatedAt.getTime() : (a.createdAt instanceof Date ? a.createdAt.getTime() : 0);
      const bTime = b.updatedAt instanceof Date ? b.updatedAt.getTime() : (b.createdAt instanceof Date ? b.createdAt.getTime() : 0);
      return bTime - aTime;
    })
    .slice(0, 3);

  const getStatusBadge = (status: string, id: string, type: 'invoice' | 'quotation') => {
    const variants: Record<string, any> = {
      paid: { variant: 'success' as const, icon: CheckCircle2, color: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200' },
      accepted: { variant: 'success' as const, icon: CheckCircle2, color: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200' },
      sent: { variant: 'info' as const, icon: Clock, color: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200' },
      draft: { variant: 'warning' as const, icon: Clock, color: 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200' },
      cancelled: { variant: 'destructive' as const, icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200' },
      declined: { variant: 'destructive' as const, icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200' },
      expired: { variant: 'destructive' as const, icon: XCircle, color: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200' },
      overdue: { variant: 'destructive' as const, icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200' },
    };

    const config = variants[status] || variants.draft;
    const Icon = config.icon;

    const options = type === 'invoice'
      ? (['draft', 'sent', 'paid', 'cancelled'] as const)
      : (['draft', 'sent', 'accepted', 'declined', 'expired'] as const);

    return (
      <DropdownMenu>
        <DropdownMenuTrigger className="outline-none focus:outline-none border-0 p-0 m-0 bg-transparent inline-flex align-middle cursor-pointer">
          <Badge className={`${config.color || 'bg-gray-100'} flex items-center gap-1 w-fit border cursor-pointer transition-all hover:scale-105`}>
            <Icon className="h-3 w-3" />
            {status.charAt(0).toUpperCase() + status.slice(1)}
            <ChevronDown className="h-3 w-3 opacity-60 ml-0.5" />
          </Badge>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {options.map((opt) => (
            <DropdownMenuItem
              key={opt}
              onSelect={() => {
                if (type === 'invoice') {
                  handleUpdateInvoiceStatus(id, opt);
                } else {
                  handleUpdateQuotationStatus(id, opt);
                }
              }}
              className="flex items-center gap-2 cursor-pointer"
            >
              <span className={cn(
                "inline-flex h-2 w-2 rounded-full",
                opt === 'paid' || opt === 'accepted' ? 'bg-green-500' :
                opt === 'sent' ? 'bg-blue-500' :
                opt === 'expired' ? 'bg-orange-500' :
                opt === 'cancelled' || opt === 'declined' || (opt as string) === 'overdue' ? 'bg-red-500' : 'bg-gray-400'
              )} />
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8 pb-6 border-b">
        <div>
          <h1 className="font-heading text-3xl font-bold font-sans">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Manage your invoices and track your business
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row items-stretch sm:items-center lg:items-end xl:items-center gap-4">
          {/* Quick-Add Setup Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {isPro ? (
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleAddBusiness}
                title="Add Business"
                className="text-xs h-9"
              >
                <Building2 className="mr-1.5 h-4 w-4" />
                Add Business
              </Button>
            ) : (
              <Button 
                size="sm" 
                variant="outline"
                disabled
                title="Upgrade to Pro to add businesses"
                className="text-xs h-9 opacity-75"
              >
                <Building2 className="mr-1.5 h-4 w-4" />
                Add Business
                <Badge variant="secondary" className="ml-1.5 px-1 py-0 text-[8px] bg-amber-100 text-amber-800 border-amber-200">PRO</Badge>
              </Button>
            )}

            {isPro ? (
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleAddCustomer}
                title="Add Customer"
                className="text-xs h-9"
              >
                <Users className="mr-1.5 h-4 w-4" />
                Add Customer
              </Button>
            ) : (
              <Button 
                size="sm" 
                variant="outline"
                disabled
                title="Upgrade to Pro to add customers"
                className="text-xs h-9 opacity-75"
              >
                <Users className="mr-1.5 h-4 w-4" />
                Add Customer
                <Badge variant="secondary" className="ml-1.5 px-1 py-0 text-[8px] bg-amber-100 text-amber-800 border-amber-200">PRO</Badge>
              </Button>
            )}

            {isPro ? (
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleAddItem}
                title="Add Item"
                className="text-xs h-9"
              >
                <Package className="mr-1.5 h-4 w-4" />
                Add Item
              </Button>
            ) : (
              <Button 
                size="sm" 
                variant="outline"
                disabled
                title="Upgrade to Pro to add items to inventory"
                className="text-xs h-9 opacity-75"
              >
                <Package className="mr-1.5 h-4 w-4" />
                Add Item
                <Badge variant="secondary" className="ml-1.5 px-1 py-0 text-[8px] bg-amber-100 text-amber-800 border-amber-200">PRO</Badge>
              </Button>
            )}

            {isPro ? (
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleAddTax}
                title="Tax Setup"
                className="text-xs h-9"
              >
                <Percent className="mr-1.5 h-4 w-4" />
                Tax Setup
              </Button>
            ) : (
              <Button 
                size="sm" 
                variant="outline"
                disabled
                title="Upgrade to Pro for predefined tax setup"
                className="text-xs h-9 opacity-75"
              >
                <Percent className="mr-1.5 h-4 w-4" />
                Tax Setup
                <Badge variant="secondary" className="ml-1.5 px-1 py-0 text-[8px] bg-amber-100 text-amber-800 border-amber-200">PRO</Badge>
              </Button>
            )}
          </div>

          {/* Divider */}
          <div className="hidden sm:block lg:hidden xl:block h-6 w-px bg-border" />
          <div className="hidden lg:block xl:hidden w-16 h-px bg-border my-0.5" />

          {/* Core Document Creation Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/quotations/new">
              <Button size="default" variant="outline" className="border-primary text-primary hover:bg-primary/5 font-semibold h-10">
                <Plus className="mr-2 h-4 w-4" />
                Create Quotation
              </Button>
            </Link>
            <Link to="/invoices/new">
              <Button size="default" className="font-semibold shadow-md h-10">
                <Plus className="mr-2 h-4 w-4" />
                Create Invoice
              </Button>
            </Link>
          </div>
        </div>
      </div>


      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Documents Overview</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{invoices.length} Invoices</div>
            <div className="text-lg font-bold mt-1">{quotations.length} Quotations & Estimates</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
            <p className="text-xs text-muted-foreground mt-1">Completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unpaid</CardTitle>
            <Clock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.unpaid}</div>
            <p className="text-xs text-muted-foreground mt-1">Pending payment</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden min-h-[145px]">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="relative">
            <div className={cn("space-y-2", !isPro && "blur-[3.5px] select-none pointer-events-none")}>
              {stats.hasMixedCurrencies ? (
                <div>
                  <div className="text-lg font-bold mb-2">Mixed Currencies</div>
                  <div className="grid grid-cols-3 gap-x-4 gap-y-1.5 text-sm font-medium text-left w-full mb-2">
                    {Object.entries(stats.revenueByCurrency).map(([currency, amount]) => (
                      <div key={currency} className="text-muted-foreground whitespace-nowrap">
                        {formatCurrency(amount, currency)}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-2xl font-bold mb-2">
                  {formatCurrency(stats.revenueByCurrency[stats.primaryCurrency] || 0, stats.primaryCurrency)}
                </div>
              )}
              <p className="text-xs text-muted-foreground">From paid invoices</p>
            </div>

            {!isPro && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 pb-10 bg-white/80 backdrop-blur-[1px]">
                <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full mb-1.5 shadow-sm">
                  <Crown className="h-3 w-3 text-amber-600 animate-pulse" />
                  PRO FEATURE
                </div>
                <p className="text-[11px] text-muted-foreground font-medium leading-relaxed mb-2.5 max-w-[210px]">
                  Unlock revenue totals & advanced cash flow metrics.
                </p>
                <Link to="/subscription">
                  <Button size="sm" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white border-0 font-semibold h-8 text-xs px-4 rounded-lg shadow-md transition-all duration-200">
                    Upgrade to Pro
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Invoices Column */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Recent Invoices</CardTitle>
                <CardDescription>Your latest invoice activity</CardDescription>
              </div>
              <Link to="/invoices">
                <Button variant="outline" size="sm">
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentInvoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No invoices yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first invoice to get started
                </p>
                <Link to="/invoices/new">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Invoice
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {recentInvoices.map((invoice) => (
                  <div
                    key={invoice.invoiceId}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h4 className="font-semibold truncate">{invoice.invoiceNumber || 'Untitled'}</h4>
                        {getStatusBadge(invoice.status, invoice.invoiceId, 'invoice')}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {invoice.billTo?.name || invoice.billTo?.companyName || 'No client'} • {invoice.issueDate ? format(invoice.issueDate, 'MMM dd, yyyy') : 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto border-t sm:border-t-0 pt-3 sm:pt-0">
                      <div className="text-left sm:text-right mr-2">
                        <p className="font-semibold text-base">
                          {formatCurrency(invoice.totalAmount || 0, invoice.currency)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/invoices/preview/${invoice.invoiceId}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="View Invoice">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={`/invoices/edit/${invoice.invoiceId}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit Invoice">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Quotations Column */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Recent Quotations & Estimates</CardTitle>
                <CardDescription>Your latest quote/estimate activity</CardDescription>
              </div>
              <Link to="/quotations">
                <Button variant="outline" size="sm">
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {(([...quotations].sort((a, b) => {
              const aTime = a.updatedAt instanceof Date ? a.updatedAt.getTime() : (a.createdAt instanceof Date ? a.createdAt.getTime() : 0);
              const bTime = b.updatedAt instanceof Date ? b.updatedAt.getTime() : (b.createdAt instanceof Date ? b.createdAt.getTime() : 0);
              return bTime - aTime;
            }).slice(0, 3)) as typeof quotations).length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No quotations yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first quotation or estimate to get started
                </p>
                <Link to="/quotations/new">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Quotation
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {([...quotations].sort((a, b) => {
                  const aTime = a.updatedAt instanceof Date ? a.updatedAt.getTime() : (a.createdAt instanceof Date ? a.createdAt.getTime() : 0);
                  const bTime = b.updatedAt instanceof Date ? b.updatedAt.getTime() : (b.createdAt instanceof Date ? b.createdAt.getTime() : 0);
                  return bTime - aTime;
                }).slice(0, 3)).map((quote) => (
                  <div
                    key={quote.quotationId}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h4 className="font-semibold truncate">{quote.quotationNumber || 'Untitled'}</h4>
                        {getStatusBadge(quote.status, quote.quotationId, 'quotation')}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {quote.billTo?.name || quote.billTo?.companyName || 'No client'} • {quote.issueDate ? format(quote.issueDate, 'MMM dd, yyyy') : 'N/A'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-between sm:justify-end gap-3 w-full sm:w-auto border-t sm:border-t-0 pt-3 sm:pt-0">
                      <div className="text-left sm:text-right mr-2">
                        <p className="font-semibold text-base">
                          {formatCurrency(quote.totalAmount || 0, quote.currency)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/invoices/new?fromQuotation=${quote.quotationId}`}>
                          <Button variant="outline" size="sm" className="text-xs" title="Create Invoice from Quotation">
                            Create Invoice
                          </Button>
                        </Link>
                        <Link to={`/quotations/preview/${quote.quotationId}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="View Quotation">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={`/quotations/edit/${quote.quotationId}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit Quotation">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Profiles, Clients, Items & Taxes Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Business Profiles Card */}
        <Card className="relative overflow-hidden">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Business Profiles</CardTitle>
                <CardDescription>Your saved company profiles</CardDescription>
              </div>
              {isPro && (
                <Button variant="outline" size="sm" onClick={handleAddBusiness}>
                  <Plus className="h-4 w-4 mr-1" /> Add New
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isPro ? (
              businesses.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">No business profiles yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {businesses.slice(0, 5).map((biz) => (
                    <div key={biz.businessId} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div className="flex-1 min-w-0 mr-4">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold text-sm truncate">{biz.name}</h4>
                          {biz.isDefault && (
                            <Badge variant="success" className="text-[9px] px-1.5 py-0">Default</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{biz.email} {biz.phone && `• ${biz.phone}`}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit Business" onClick={() => handleEditBusiness(biz)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" title="Delete Business" onClick={() => handleDeleteBusiness(biz.businessId)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="space-y-4 filter blur-[2px] select-none pointer-events-none">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Acme Corp</h4>
                      <p className="text-xs text-muted-foreground">contact@acme.com • +1 555-0199</p>
                    </div>
                  </div>
                  <Badge variant="success" className="text-[10px]">Default</Badge>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Stark Industries</h4>
                      <p className="text-xs text-muted-foreground">billing@stark.com • +1 555-0100</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isPro && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 bg-white/90 backdrop-blur-[2px] z-10">
                <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full mb-1.5 shadow-sm">
                  <Crown className="h-3 w-3 text-amber-600 animate-pulse" />
                  PRO FEATURE
                </div>
                <p className="text-[11px] text-muted-foreground font-medium leading-relaxed mb-2.5 max-w-[240px]">
                  Save multiple business profiles for instant 1-click billing.
                </p>
                <Link to="/subscription">
                  <Button size="sm" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white border-0 font-semibold h-8 text-xs px-4 rounded-lg shadow-md transition-all duration-200">
                    Upgrade to Pro
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client Address Book Card */}
        <Card className="relative overflow-hidden">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Client Address Book</CardTitle>
                <CardDescription>Your saved clients & customers</CardDescription>
              </div>
              {isPro && (
                <Button variant="outline" size="sm" onClick={handleAddCustomer}>
                  <Plus className="h-4 w-4 mr-1" /> Add New
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isPro ? (
              customers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">No clients in your address book yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {customers.slice(0, 5).map((cust) => (
                    <div key={cust.customerId} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div className="flex-1 min-w-0 mr-4">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold text-sm truncate">{cust.name}</h4>
                          {cust.isDefault && (
                            <Badge variant="success" className="text-[9px] px-1.5 py-0">Default</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{cust.companyName || 'Individual'} {cust.email && `• ${cust.email}`}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit Client" onClick={() => handleEditCustomer(cust)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" title="Delete Client" onClick={() => handleDeleteCustomer(cust.customerId)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="space-y-4 filter blur-[2px] select-none pointer-events-none">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      <Users className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Wayne Enterprises</h4>
                      <p className="text-xs text-muted-foreground">Bruce Wayne • bruce@wayne.com</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      <Users className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Oscorp Technologies</h4>
                      <p className="text-xs text-muted-foreground">Norman Osborn • norman@oscorp.com</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isPro && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 bg-white/90 backdrop-blur-[1px] z-10">
                <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full mb-1.5 shadow-sm">
                  <Crown className="h-3 w-3 text-amber-600 animate-pulse" />
                  PRO FEATURE
                </div>
                <p className="text-[11px] text-muted-foreground font-medium leading-relaxed mb-2.5 max-w-[240px]">
                  Build a reusable client directory to auto-fill invoices.
                </p>
                <Link to="/subscription">
                  <Button size="sm" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white border-0 font-semibold h-8 text-xs px-4 rounded-lg shadow-md transition-all duration-200">
                    Upgrade to Pro
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inventory Items Card */}
        <Card className="relative overflow-hidden">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Inventory Items & Services</CardTitle>
                <CardDescription>Your saved items, services, & products</CardDescription>
              </div>
              {isPro && (
                <Button variant="outline" size="sm" onClick={handleAddItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add New
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isPro ? (
              inventoryItems.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">No items or services in your database yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {inventoryItems.slice(0, 5).map((item) => (
                    <div key={item.itemId} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div className="flex-1 min-w-0 mr-4">
                        <h4 className="font-semibold text-sm truncate">{item.name}</h4>
                        <p className="text-xs text-muted-foreground truncate">Code: {item.code || 'N/A'} {item.description && `• ${item.description}`}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="text-right mr-1">
                          <p className="font-semibold text-sm">{formatCurrency(Number(item.rate) || 0, userProfile?.defaultCurrency || 'USD')}</p>
                          <p className="text-[10px] text-muted-foreground">per {item.unit || 'pcs'}</p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit Item" onClick={() => handleEditItem(item)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" title="Delete Item" onClick={() => handleDeleteItem(item.itemId)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="space-y-4 filter blur-[2px] select-none pointer-events-none">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      <Package className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Consulting Hour</h4>
                      <p className="text-xs text-muted-foreground">Code: CONS-01 • Expert advisory</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm">{formatCurrency(150, userProfile?.defaultCurrency || 'USD')}</p>
                    <p className="text-[10px] text-muted-foreground">per hr</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      <Package className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Web Development</h4>
                      <p className="text-xs text-muted-foreground">Code: DEV-01 • Custom website package</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm">{formatCurrency(5000, userProfile?.defaultCurrency || 'USD')}</p>
                    <p className="text-[10px] text-muted-foreground">per unit</p>
                  </div>
                </div>
              </div>
            )}

            {!isPro && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 bg-white/90 backdrop-blur-[1px] z-10">
                <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full mb-1.5 shadow-sm">
                  <Crown className="h-3 w-3 text-amber-600 animate-pulse" />
                  PRO FEATURE
                </div>
                <p className="text-[11px] text-muted-foreground font-medium leading-relaxed mb-2.5 max-w-[240px]">
                  Store and auto-fill your inventory and service catalog.
                </p>
                <Link to="/subscription">
                  <Button size="sm" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white border-0 font-semibold h-8 text-xs px-4 rounded-lg shadow-md transition-all duration-200">
                    Upgrade to Pro
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Predefined Taxes Card */}
        <Card className="relative overflow-hidden">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Predefined Taxes</CardTitle>
                <CardDescription>Your saved tax rates</CardDescription>
              </div>
              {isPro && (
                <Button variant="outline" size="sm" onClick={handleAddTax}>
                  <Plus className="h-4 w-4 mr-1" /> Add New
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isPro ? (
              (!userProfile?.predefinedTaxes || userProfile.predefinedTaxes.length === 0) ? (
                <div className="text-center py-8">
                  <Percent className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">No predefined taxes configured yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userProfile.predefinedTaxes.slice(0, 5).map((tax, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div className="flex-1 min-w-0 mr-4">
                        <h4 className="font-semibold text-sm truncate">{tax.label}</h4>
                        <p className="text-xs text-muted-foreground">
                          {tax.type === 'percentage' ? 'Percentage' : 'Flat Amount'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="text-right mr-1">
                          <p className="font-semibold text-sm text-primary">
                            {tax.type === 'percentage' ? `${tax.value}%` : formatCurrency(Number(tax.value) || 0, userProfile?.defaultCurrency || 'USD')}
                          </p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit Tax" onClick={() => handleEditTax(index)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" title="Delete Tax" onClick={() => handleDeleteTax(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="space-y-4 filter blur-[2px] select-none pointer-events-none">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      <Percent className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">VAT</h4>
                      <p className="text-xs text-muted-foreground">Percentage</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm">15%</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      <Percent className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">GST</h4>
                      <p className="text-xs text-muted-foreground">Percentage</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm">6%</p>
                  </div>
                </div>
              </div>
            )}

            {!isPro && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 bg-white/90 backdrop-blur-[1px] z-10">
                <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full mb-1.5 shadow-sm">
                  <Crown className="h-3 w-3 text-amber-600 animate-pulse" />
                  PRO FEATURE
                </div>
                <p className="text-[11px] text-muted-foreground font-medium leading-relaxed mb-2.5 max-w-[240px]">
                  Preset tax rates like VAT/GST/SST for fast selection.
                </p>
                <Link to="/subscription">
                  <Button size="sm" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white border-0 font-semibold h-8 text-xs px-4 rounded-lg shadow-md transition-all duration-200">
                    Upgrade to Pro
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>

    {/* Business Dialog */}
    <Dialog open={showBusinessDialog} onOpenChange={setShowBusinessDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingBusiness ? 'Edit Business Profile' : 'Add Business Profile'}</DialogTitle>
          <DialogDescription>
            {editingBusiness ? 'Update the details for your business profile' : 'Add a new business profile for your invoices'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Logo Upload Section */}
          <div className="space-y-2 border-b pb-4">
            <Label>Business Logo</Label>
            {!isPro ? (
              <div className="border border-dashed rounded-lg p-4 bg-slate-50 flex items-center justify-between text-left">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0">
                    <Crown className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">Business Logo (Pro Feature)</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Upgrade to Pro to upload logo and brand invoices/quotations.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white font-bold shrink-0"
                  onClick={() => {
                    setShowBusinessDialog(false);
                    navigate('/subscription');
                  }}
                >
                  Upgrade
                </Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                {businessForm.logoUrl ? (
                  <div className="relative group w-32 h-20 bg-slate-100 border rounded-lg overflow-hidden flex items-center justify-center">
                    <img
                      src={businessForm.logoUrl}
                      alt="Logo preview"
                      className="max-h-full max-w-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold"
                    >
                      Remove Logo
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-20 bg-slate-50 border border-dashed rounded-lg flex items-center justify-center text-muted-foreground">
                    <ImageIcon className="w-8 h-8 opacity-45" />
                  </div>
                )}

                <div className="flex-1 space-y-2 text-left">
                  <div className="flex items-center gap-2">
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={logoUploading}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={logoUploading}
                      onClick={() => document.getElementById('logo-upload')?.click()}
                    >
                      {logoUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {businessForm.logoUrl ? 'Change Logo' : 'Upload Logo'}
                        </>
                      )}
                    </Button>
                    {businessForm.logoUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleRemoveLogo}
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Supported formats: PNG, JPG, SVG. Max size: 2MB. Recommended: Landscape ratio.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="businessName">
                Business Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="businessName"
                value={businessForm.name}
                onChange={(e) => {
                  setBusinessForm({ ...businessForm, name: e.target.value });
                  if (businessErrors.name) {
                    setBusinessErrors((prev) => ({ ...prev, name: false }));
                  }
                }}
                placeholder="Your Company Name"
                className={cn(businessErrors.name && "border-destructive focus-visible:ring-destructive")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessEmail">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="businessEmail"
                type="email"
                value={businessForm.email}
                onChange={(e) => {
                  setBusinessForm({ ...businessForm, email: e.target.value });
                  if (businessErrors.email) {
                    setBusinessErrors((prev) => ({ ...prev, email: false }));
                  }
                }}
                placeholder="contact@company.com"
                className={cn(businessErrors.email && "border-destructive focus-visible:ring-destructive")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessPhone">Phone</Label>
              <Input
                id="businessPhone"
                value={businessForm.phone}
                onChange={(e) => {
                  setBusinessForm({ ...businessForm, phone: e.target.value });
                  if (businessErrors.phone) {
                    setBusinessErrors((prev) => ({ ...prev, phone: false }));
                  }
                }}
                placeholder="+1 234 567 8900"
                className={cn(businessErrors.phone && "border-destructive focus-visible:ring-destructive")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessWebsite">Website</Label>
              <Input
                id="businessWebsite"
                value={businessForm.website}
                onChange={(e) => setBusinessForm({ ...businessForm, website: e.target.value })}
                placeholder="https://yourcompany.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="businessAddress">Address</Label>
            <Textarea
              id="businessAddress"
              value={businessForm.address}
              onChange={(e) => setBusinessForm({ ...businessForm, address: e.target.value })}
              placeholder="123 Business Street, City, State, ZIP"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="businessTaxNumber">Tax Number</Label>
              <Input
                id="businessTaxNumber"
                value={businessForm.taxNumber}
                onChange={(e) => setBusinessForm({ ...businessForm, taxNumber: e.target.value })}
                placeholder="Tax ID / VAT Number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessBankDetails">Bank Details</Label>
              <Input
                id="businessBankDetails"
                value={businessForm.bankDetails}
                onChange={(e) => setBusinessForm({ ...businessForm, bankDetails: e.target.value })}
                placeholder="Bank account info"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="stripePaymentLink" className="flex items-center justify-between">
                <span>Stripe Payment Link</span>
                {userProfile?.plan !== 'pro' && (
                  <Badge variant="secondary" className="text-[10px] bg-amber-100 hover:bg-amber-100 text-amber-800 border-amber-200 py-0 px-1.5 font-semibold">PRO Only</Badge>
                )}
              </Label>
              <Input
                id="stripePaymentLink"
                value={(businessForm as any).stripePaymentLink || ''}
                onChange={(e) => {
                  setBusinessForm({ ...businessForm, stripePaymentLink: e.target.value });
                  if (businessErrors.stripePaymentLink) {
                    setBusinessErrors((prev) => ({ ...prev, stripePaymentLink: false }));
                  }
                }}
                placeholder="https://buy.stripe.com/..."
                disabled={userProfile?.plan !== 'pro'}
                className={cn(businessErrors.stripePaymentLink && "border-destructive focus-visible:ring-destructive")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="paypalLink" className="flex items-center justify-between">
                <span>PayPal.me Link</span>
                {userProfile?.plan !== 'pro' && (
                  <Badge variant="secondary" className="text-[10px] bg-amber-100 hover:bg-amber-100 text-amber-800 border-amber-200 py-0 px-1.5 font-semibold">PRO Only</Badge>
                )}
              </Label>
              <Input
                id="paypalLink"
                value={(businessForm as any).paypalLink || ''}
                onChange={(e) => {
                  setBusinessForm({ ...businessForm, paypalLink: e.target.value });
                  if (businessErrors.paypalLink) {
                    setBusinessErrors((prev) => ({ ...prev, paypalLink: false }));
                  }
                }}
                placeholder="https://paypal.me/..."
                disabled={userProfile?.plan !== 'pro'}
                className={cn(businessErrors.paypalLink && "border-destructive focus-visible:ring-destructive")}
              />
            </div>
          </div>

          {(() => {
            const hasPaymentLinks = !!(
              (businessForm as any).stripePaymentLink?.trim() ||
              (businessForm as any).paypalLink?.trim()
            );
            const isCheckboxDisabled = userProfile?.plan !== 'pro' || !hasPaymentLinks;
            return (
              <div className="flex items-center space-x-2 py-1">
                <input
                  type="checkbox"
                  id="enableAutoApplyPaymentLinks"
                  checked={!isCheckboxDisabled && !((businessForm as any).disableAutoApplyPaymentLinks)}
                  onChange={(e) => setBusinessForm({ ...businessForm, disableAutoApplyPaymentLinks: !e.target.checked })}
                  disabled={isCheckboxDisabled}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
                />
                <Label
                  htmlFor="enableAutoApplyPaymentLinks"
                  className={`text-xs font-normal cursor-pointer flex items-center gap-1.5 ${isCheckboxDisabled ? 'text-slate-400' : 'text-slate-600'}`}
                >
                  Enable auto-apply payment links on new invoices
                  {userProfile?.plan !== 'pro' && (
                    <Badge variant="secondary" className="text-[9px] bg-amber-100 text-amber-800 border-amber-200 py-0 px-1 font-semibold">PRO</Badge>
                  )}
                  {userProfile?.plan === 'pro' && !hasPaymentLinks && (
                    <span className="text-[10px] text-slate-400 italic">(add a payment link to enable)</span>
                  )}
                </Label>
              </div>
            );
          })()}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isDefault"
              checked={businessForm.isDefault}
              onChange={(e) => setBusinessForm({ ...businessForm, isDefault: e.target.checked })}
              className="rounded border-gray-300"
            />
            <Label htmlFor="isDefault" className="cursor-pointer">
              Set as default business profile
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowBusinessDialog(false)}
            disabled={savingBusiness}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveBusiness}
            disabled={!businessForm.name || !businessForm.email || savingBusiness}
          >
            {savingBusiness ? 'Saving...' : (editingBusiness ? 'Save Changes' : 'Add Business')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Customer Dialog */}
    <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingCustomer ? 'Edit Customer Profile' : 'Add Customer Profile'}</DialogTitle>
          <DialogDescription>
            {editingCustomer ? 'Update the details for your customer profile' : 'Add a new customer profile for quick invoice creation'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">
                Customer Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="customerName"
                value={customerForm.name}
                onChange={(e) => {
                  setCustomerForm({ ...customerForm, name: e.target.value });
                  if (customerErrors.name) {
                    setCustomerErrors((prev) => ({ ...prev, name: false }));
                  }
                }}
                placeholder="John Doe"
                className={cn(customerErrors.name && "border-destructive focus-visible:ring-destructive")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customerCompany">Company Name</Label>
              <Input
                id="customerCompany"
                value={customerForm.companyName}
                onChange={(e) => setCustomerForm({ ...customerForm, companyName: e.target.value })}
                placeholder="Customer Company Inc."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customerEmail">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="customerEmail"
                type="email"
                value={customerForm.email}
                onChange={(e) => {
                  setCustomerForm({ ...customerForm, email: e.target.value });
                  if (customerErrors.email) {
                    setCustomerErrors((prev) => ({ ...prev, email: false }));
                  }
                }}
                placeholder="customer@email.com"
                className={cn(customerErrors.email && "border-destructive focus-visible:ring-destructive")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customerPhone">Phone</Label>
              <Input
                id="customerPhone"
                value={customerForm.phone}
                onChange={(e) => {
                  setCustomerForm({ ...customerForm, phone: e.target.value });
                  if (customerErrors.phone) {
                    setCustomerErrors((prev) => ({ ...prev, phone: false }));
                  }
                }}
                placeholder="+1 234 567 8900"
                className={cn(customerErrors.phone && "border-destructive focus-visible:ring-destructive")}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="customerAddress">Address</Label>
            <Textarea
              id="customerAddress"
              value={customerForm.address}
              onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
              placeholder="123 Customer Street, City, State, ZIP"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customerTaxNumber">Tax Number</Label>
            <Input
              id="customerTaxNumber"
              value={customerForm.taxNumber}
              onChange={(e) => setCustomerForm({ ...customerForm, taxNumber: e.target.value })}
              placeholder="Tax ID / VAT Number"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="customerDefault"
              checked={customerForm.isDefault}
              onChange={(e) => setCustomerForm({ ...customerForm, isDefault: e.target.checked })}
              className="rounded border-gray-300"
            />
            <Label htmlFor="customerDefault" className="cursor-pointer">
              Set as default customer profile
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowCustomerDialog(false)}
            disabled={savingCustomer}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCustomer}
            disabled={!customerForm.name || !customerForm.email || savingCustomer}
          >
            {savingCustomer ? 'Saving...' : (editingCustomer ? 'Save Changes' : 'Add Customer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Item Dialog */}
    <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingItem ? 'Edit Item / Service' : 'Add Item / Service'}</DialogTitle>
          <DialogDescription>
            {editingItem ? 'Update the details for your inventory item or service' : 'Add a new item or service to your database for single-click invoice creation'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="itemCode">
                Item Code / SKU <span className="text-destructive">*</span>
              </Label>
              <Input
                id="itemCode"
                value={itemForm.code}
                onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })}
                placeholder="e.g. SRV-001, ITEM-A"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="itemName">
                Item Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="itemName"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="e.g. Consultant Fee, Web Development"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="itemRate">
                Unit Price / Rate ($) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="itemRate"
                type="number"
                step="0.01"
                min="0"
                value={itemForm.rate ?? ''}
                onChange={(e) => setItemForm({ ...itemForm, rate: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="itemUnit">Unit</Label>
              <Input
                id="itemUnit"
                value={itemForm.unit}
                onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                placeholder="e.g. pcs, hrs, days"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="itemDescription">Description / Default Notes</Label>
            <Textarea
              id="itemDescription"
              value={itemForm.description}
              onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
              placeholder="Detailed description of the item"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowItemDialog(false)}
            disabled={savingItem}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveItem}
            disabled={!itemForm.code || !itemForm.name || savingItem}
          >
            {savingItem ? 'Saving...' : (editingItem ? 'Save Changes' : 'Add Item')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Tax Predefined Setup Dialog */}
    <Dialog open={showTaxDialog} onOpenChange={setShowTaxDialog}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingTaxIndex !== null ? 'Edit Predefined Tax Rate' : 'Add Predefined Tax Rate'}</DialogTitle>
          <DialogDescription>
            {editingTaxIndex !== null ? 'Update the details for your predefined tax rate' : 'Set up a saved tax profile to quickly apply to your invoices and quotations.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="taxLabel">
              Tax Label / Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="taxLabel"
              value={taxForm.label}
              onChange={(e) => setTaxForm({ ...taxForm, label: e.target.value })}
              placeholder="e.g. VAT 10%, SST 8%"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="taxType">Tax Type</Label>
              <Select
                value={taxForm.type}
                onValueChange={(val: any) => setTaxForm({ ...taxForm, type: val })}
              >
                <SelectTrigger id="taxType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage %</SelectItem>
                  <SelectItem value="vat">VAT %</SelectItem>
                  <SelectItem value="sst">SST %</SelectItem>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxValue">
                Rate / Value <span className="text-destructive">*</span>
              </Label>
              <Input
                id="taxValue"
                type="number"
                step="0.01"
                min="0"
                value={taxForm.value}
                onChange={(e) => setTaxForm({ ...taxForm, value: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowTaxDialog(false)}
            disabled={savingTax}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveTax}
            disabled={!taxForm.label || taxForm.value === '' || savingTax}
          >
            {savingTax ? 'Saving...' : (editingTaxIndex !== null ? 'Save Changes' : 'Add Tax')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
